import { BiSolidTrash } from "solid-icons/bi";
import { createMemo, Index, Show } from "solid-js";
import { produce } from "solid-js/store";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxTrigger,
} from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROFICIENCY_LEVELS,
  type ProficiencyLevel,
  SPOKEN_LANGUAGES,
} from "@/constants/languages";
import { useOnboardingStore } from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";
export const Locale = () => {
  const [store, setStore] = useOnboardingStore();
  const { t } = useI18n();
  const availableLanguages = createMemo(() =>
    SPOKEN_LANGUAGES.filter(
      (lang) =>
        !store.locale.value.spoken_languages.some((l) => l.code === lang.code),
    ),
  );

  const addNewLanguage = () => {
    const newLanguage = availableLanguages()[0];
    if (newLanguage) {
      setStore(
        produce((draft) => {
          draft.locale.value.spoken_languages = [
            ...draft.locale.value.spoken_languages,
            {
              code: newLanguage.code,
              name: newLanguage.name,
              level: "Madrelingua" as ProficiencyLevel,
            },
          ];
        }),
      );
    }
  };

  const removeLanguage = (code: string) => {
    setStore(
      produce((draft) => {
        draft.locale.value.spoken_languages =
          draft.locale.value.spoken_languages.filter(
            (lang) => lang.code !== code,
          );
      }),
    );
  };

  const updateLanguageLevel = (code: string, level: ProficiencyLevel) => {
    setStore(
      produce((draft) => {
        draft.locale.value.spoken_languages =
          draft.locale.value.spoken_languages.map((lang) =>
            lang.code === code ? { ...lang, level } : lang,
          );
      }),
    );
  };

  const updateLanguageName = (code: string, name: string) => {
    setStore(
      produce((draft) => {
        draft.locale.value.spoken_languages =
          draft.locale.value.spoken_languages.map((lang) =>
            lang.code === code ? { ...lang, name } : lang,
          );
      }),
    );
  };

  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>{t("onboarding.steps.locale.title")}</OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.locale.description")}
        </OnboardingDescription>
      </div>
      <div class="flex gap-4 justify-between items-end">
        <div class="flex flex-col gap-8 w-full">
          <div class="grid grid-cols-3 gap-4">
            <p>{t("onboarding.steps.locale.spokenLanguages")}</p>
            <p>{t("onboarding.steps.locale.proficiency")}</p>
            <p class="text-right">{t("common.actions")}</p>
          </div>
          <Index each={store.locale.value.spoken_languages}>
            {(lang) => (
              <div class="grid grid-cols-3 gap-4 border-b border-gray-200 pb-4">
                <Combobox
                  class="bg-white"
                  options={SPOKEN_LANGUAGES.map((lang) => lang.name)}
                  value={lang().name}
                  itemComponent={(props) => (
                    <ComboboxItem {...props}>
                      {props.item.rawValue}
                    </ComboboxItem>
                  )}
                  onChange={(value) => updateLanguageName(lang().code, value!)}
                >
                  <ComboboxTrigger class="h-10">
                    <ComboboxInput value={lang().name} />
                  </ComboboxTrigger>
                  <ComboboxContent class="max-h-[200px]" />
                </Combobox>
                <Select
                  value={lang().level}
                  onChange={(value) => updateLanguageLevel(lang().code, value!)}
                  options={PROFICIENCY_LEVELS}
                  itemComponent={(props) => (
                    <SelectItem item={props.item}>
                      {props.item.rawValue}
                    </SelectItem>
                  )}
                >
                  <SelectTrigger class="bg-white">
                    <SelectValue>{lang().level}</SelectValue>
                  </SelectTrigger>
                  <SelectContent />
                </Select>
                <Show
                  when={lang().code !== "en"}
                  fallback={
                    <p class="text-muted-foreground text-right">Default</p>
                  }
                >
                  <div class="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => removeLanguage(lang().code)}
                    >
                      <BiSolidTrash />
                    </Button>
                  </div>
                </Show>
              </div>
            )}
          </Index>
          <Button
            disabled={availableLanguages().length === 0}
            onClick={() => addNewLanguage()}
            class="w-fit"
            variant="outline"
          >
            {t("onboarding.steps.locale.addLanguage")}
          </Button>
        </div>
      </div>
    </div>
  );
};
